import { useState } from "react";
import { FactoryListing } from "@/mocks/factoryDashboard";

interface Props {
  listings: FactoryListing[];
  onUpdateCommission: (id: string, commission: number) => void;
  onToggleStatus: (id: string) => void;
}

const statusColors: Record<string, string> = {
  active: "bg-green-100 text-green-700",
  paused: "bg-yellow-100 text-yellow-700",
  draft: "bg-gray-100 text-gray-500",
};

const statusIcons: Record<string, string> = {
  active: "ri-checkbox-circle-line",
  paused: "ri-pause-circle-line",
  draft: "ri-draft-line",
};

export default function ProductListingTable({ listings, onUpdateCommission, onToggleStatus }: Props) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const filtered = filterStatus === "all" ? listings : listings.filter((l) => l.status === filterStatus);

  const handleEditStart = (listing: FactoryListing) => {
    setEditingId(listing.id);
    setEditValue(String(listing.commission));
  };

  const handleEditSave = (id: string) => {
    const val = parseFloat(editValue);
    if (!isNaN(val) && val >= 0 && val <= 50) {
      onUpdateCommission(id, val);
    }
    setEditingId(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent, id: string) => {
    if (e.key === "Enter") handleEditSave(id);
    if (e.key === "Escape") setEditingId(null);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-5 border-b border-gray-100">
        <div>
          <h2 className="text-base font-bold text-gray-900">Product Listings</h2>
          <p className="text-xs text-gray-400 mt-0.5">Manage your products and set commission rates for resellers</p>
        </div>
        <div className="flex items-center gap-2">
          {["all", "active", "paused", "draft"].map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors cursor-pointer whitespace-nowrap ${filterStatus === s ? "bg-orange-500 text-white" : "bg-gray-50 text-gray-600 hover:bg-gray-100"}`}
            >
              {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Product</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3 hidden md:table-cell">Category</th>
              <th className="text-right text-xs font-semibold text-gray-500 px-4 py-3">Price</th>
              <th className="text-right text-xs font-semibold text-gray-500 px-4 py-3 hidden sm:table-cell">Stock</th>
              <th className="text-center text-xs font-semibold text-gray-500 px-4 py-3">Commission %</th>
              <th className="text-right text-xs font-semibold text-gray-500 px-4 py-3 hidden lg:table-cell">Sales</th>
              <th className="text-right text-xs font-semibold text-gray-500 px-4 py-3 hidden lg:table-cell">Revenue</th>
              <th className="text-center text-xs font-semibold text-gray-500 px-4 py-3">Status</th>
              <th className="text-center text-xs font-semibold text-gray-500 px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map((listing) => (
              <tr key={listing.id} className="hover:bg-gray-50/50 transition-colors">
                {/* Product */}
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                      <img src={listing.image} alt={listing.title} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate max-w-[180px]">{listing.title}</p>
                      {listing.badge && (
                        <span className="inline-block text-xs font-medium px-1.5 py-0.5 bg-orange-50 text-orange-600 rounded mt-0.5">
                          {listing.badge}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                {/* Category */}
                <td className="px-4 py-4 hidden md:table-cell">
                  <span className="text-xs text-gray-500">{listing.category}</span>
                </td>
                {/* Price */}
                <td className="px-4 py-4 text-right">
                  <span className="text-sm font-bold text-gray-900">${listing.price.toFixed(2)}</span>
                </td>
                {/* Stock */}
                <td className="px-4 py-4 text-right hidden sm:table-cell">
                  <span className={`text-sm font-medium ${listing.stock === 0 ? "text-red-500" : listing.stock < 100 ? "text-amber-500" : "text-gray-700"}`}>
                    {listing.stock === 0 ? "Out" : listing.stock.toLocaleString()}
                  </span>
                </td>
                {/* Commission */}
                <td className="px-4 py-4 text-center">
                  {editingId === listing.id ? (
                    <div className="flex items-center justify-center gap-1">
                      <input
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, listing.id)}
                        className="w-14 text-center text-sm border border-orange-400 rounded-md px-1 py-1 outline-none"
                        min={0}
                        max={50}
                        autoFocus
                      />
                      <span className="text-xs text-gray-400">%</span>
                      <button onClick={() => handleEditSave(listing.id)} className="w-6 h-6 flex items-center justify-center text-green-600 hover:bg-green-50 rounded cursor-pointer">
                        <i className="ri-check-line text-sm" />
                      </button>
                      <button onClick={() => setEditingId(null)} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:bg-gray-100 rounded cursor-pointer">
                        <i className="ri-close-line text-sm" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleEditStart(listing)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-50 text-orange-600 text-sm font-bold rounded-lg hover:bg-orange-100 transition-colors cursor-pointer"
                    >
                      {listing.commission}%
                      <i className="ri-pencil-line text-xs" />
                    </button>
                  )}
                </td>
                {/* Sales */}
                <td className="px-4 py-4 text-right hidden lg:table-cell">
                  <span className="text-sm text-gray-700">{listing.totalSales.toLocaleString()}</span>
                </td>
                {/* Revenue */}
                <td className="px-4 py-4 text-right hidden lg:table-cell">
                  <span className="text-sm font-semibold text-green-600">
                    ${listing.totalRevenue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </td>
                {/* Status */}
                <td className="px-4 py-4 text-center">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${statusColors[listing.status]}`}>
                    <i className={statusIcons[listing.status]} />
                    {listing.status.charAt(0).toUpperCase() + listing.status.slice(1)}
                  </span>
                </td>
                {/* Actions */}
                <td className="px-4 py-4 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <button
                      onClick={() => onToggleStatus(listing.id)}
                      title={listing.status === "active" ? "Pause listing" : "Activate listing"}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${listing.status === "active" ? "text-yellow-600 hover:bg-yellow-50" : "text-green-600 hover:bg-green-50"}`}
                    >
                      <i className={listing.status === "active" ? "ri-pause-line" : "ri-play-line"} />
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center text-gray-400 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer" title="Edit product">
                      <i className="ri-edit-line" />
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center text-gray-400 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer" title="View analytics">
                      <i className="ri-bar-chart-line" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="py-16 text-center">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 bg-gray-50 rounded-full">
              <i className="ri-store-2-line text-2xl text-gray-300" />
            </div>
            <p className="text-gray-400 text-sm">No listings found</p>
          </div>
        )}
      </div>
    </div>
  );
}
